import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { downloadFile } from "./download";

const fmtEur = (v) =>
    new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
    }).format(Number(v) || 0);

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("el-GR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Convert ArrayBuffer to base64 (chunked to avoid stack overflow)
function abToBase64(ab) {
    const bytes = new Uint8Array(ab);
    let binary = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(
            null,
            bytes.subarray(i, i + CHUNK)
        );
    }
    return btoa(binary);
}

let _greekFontsCache = null;

async function loadGreekFonts() {
    if (_greekFontsCache) return _greekFontsCache;
    const [reg, bold] = await Promise.all([
        fetch("/fonts/NotoSans-Regular.ttf").then((r) => r.arrayBuffer()),
        fetch("/fonts/NotoSans-Bold.ttf").then((r) => r.arrayBuffer()),
    ]);
    _greekFontsCache = {
        regular: abToBase64(reg),
        bold: abToBase64(bold),
    };
    return _greekFontsCache;
}

async function makeGreekPDF() {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    try {
        const { regular, bold } = await loadGreekFonts();
        doc.addFileToVFS("NotoSans-Regular.ttf", regular);
        doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
        doc.addFileToVFS("NotoSans-Bold.ttf", bold);
        doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
        doc.setFont("NotoSans", "normal");
    } catch {
        // Fallback to helvetica if fonts fail to load
        doc.setFont("helvetica", "normal");
    }
    return doc;
}

export async function exportCustomerPDF({
    customer,
    sales,
    periodLabel = "",
    fileSuffix = "",
}) {
    const doc = await makeGreekPDF();
    const fontName =
        doc.getFontList().NotoSans ? "NotoSans" : "helvetica";

    const W = doc.internal.pageSize.getWidth();
    let y = 14;

    // Header
    doc.setFont(fontName, "bold");
    doc.setFontSize(18);
    doc.text("ΚΑΡΤΕΛΑ ΠΕΛΑΤΗ", W / 2, y, { align: "center" });
    y += 8;

    if (periodLabel) {
        doc.setFontSize(10);
        doc.setFont(fontName, "normal");
        doc.text(`Περίοδος: ${periodLabel}`, W / 2, y, { align: "center" });
        y += 6;
    }

    doc.setFontSize(11);
    doc.setFont(fontName, "bold");
    doc.text(`#${customer.code}  ${customer.name}`, 14, y);
    y += 6;
    doc.setFont(fontName, "normal");
    doc.setFontSize(10);
    if (customer.afm) {
        doc.text(`ΑΦΜ: ${customer.afm}`, 14, y);
        y += 5;
    }
    if (customer.phone) {
        doc.text(`Τηλέφωνο: ${customer.phone}`, 14, y);
        y += 5;
    }
    if (customer.email) {
        doc.text(`Email: ${customer.email}`, 14, y);
        y += 5;
    }
    doc.text(
        `Όριο μήνα: ${fmtEur(customer.monthly_limit)}    Υπόλοιπο: ${fmtEur(customer.remaining)}`,
        14,
        y
    );
    y += 8;

    // Summary table
    doc.setFont(fontName, "bold");
    doc.setFontSize(12);
    doc.text("ΣΥΝΟΛΙΚΕΣ ΚΙΝΗΣΕΙΣ", 14, y);
    y += 3;

    autoTable(doc, {
        startY: y,
        head: [["Ημερομηνία", "Είδη", "Καθαρή", "ΦΠΑ", "Σύνολο", "Χρεώθηκε"]],
        body: sales.map((s) => [
            fmtDate(s.created_at),
            String(s.line_items.length),
            fmtEur(s.subtotal_net),
            fmtEur(s.total_vat),
            fmtEur(s.total_gross),
            fmtEur(s.amount_charged),
        ]),
        styles: { font: fontName, fontSize: 9 },
        headStyles: {
            font: fontName,
            fontStyle: "bold",
            fillColor: [10, 10, 10],
            textColor: 255,
        },
        margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Detailed per-sale
    doc.setFont(fontName, "bold");
    doc.setFontSize(12);
    if (y > 250) {
        doc.addPage();
        y = 14;
    }
    doc.text("ΑΝΑΛΥΤΙΚΗ ΚΑΡΤΕΛΑ — ΕΙΔΗ ΑΝΑ ΚΙΝΗΣΗ", 14, y);
    y += 4;

    for (const s of sales) {
        if (y > 260) {
            doc.addPage();
            y = 14;
        }
        doc.setFont(fontName, "bold");
        doc.setFontSize(10);
        doc.text(
            `${fmtDate(s.created_at)}  •  Σύνολο ${fmtEur(s.total_gross)}`,
            14,
            y
        );
        y += 2;
        autoTable(doc, {
            startY: y,
            head: [["Κωδικός", "Είδος", "Ποσότητα", "Τιμή", "ΦΠΑ%", "Σύνολο"]],
            body: s.line_items.map((li) => [
                li.item_code,
                li.description,
                String(li.quantity),
                fmtEur(li.unit_price),
                `${li.vat_rate}%`,
                fmtEur(li.unit_price * li.quantity),
            ]),
            styles: { font: fontName, fontSize: 8 },
            headStyles: {
                font: fontName,
                fontStyle: "bold",
                fillColor: [80, 80, 80],
                textColor: 255,
            },
            margin: { left: 18, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 4;
    }

    const blob = doc.output("blob");
    downloadFile(
        blob,
        `pelaths-${customer.code}${fileSuffix || ""}.pdf`,
        "application/pdf"
    );
}

/**
 * Formats a number using Greek locale (comma as decimal separator).
 * Excel cells store as strings so display is consistent across locales.
 */
function fmtNumGR(n, decimals = 2) {
    if (n === null || n === undefined || n === "") return "";
    const num = Number(n);
    if (!Number.isFinite(num)) return "";
    return num.toLocaleString("el-GR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: false,
    });
}

/**
 * Build XLSX with two sheets: summary + details
 */
export function exportCustomerXLSX({
    customer,
    sales,
    periodLabel = "",
    fileSuffix = "",
}) {
    const wb = XLSX.utils.book_new();

    // Compute totals across visible sales
    const totalItems = sales.reduce((a, s) => a + (s.line_items?.length || 0), 0);
    const totalNet = sales.reduce((a, s) => a + Number(s.subtotal_net || 0), 0);
    const totalVat = sales.reduce((a, s) => a + Number(s.total_vat || 0), 0);
    const totalGross = sales.reduce((a, s) => a + Number(s.total_gross || 0), 0);
    const totalCharged = sales.reduce((a, s) => a + Number(s.amount_charged || 0), 0);

    const headerRows = [
        ["ΚΑΡΤΕΛΑ ΠΕΛΑΤΗ"],
        ...(periodLabel ? [[`Περίοδος: ${periodLabel}`]] : []),
        [],
        ["Κωδικός", customer.code],
        ["Όνομα", customer.name],
        ["ΑΦΜ", customer.afm || ""],
        ["Τηλέφωνο", customer.phone || ""],
        ["Email", customer.email || ""],
        ["Όριο μήνα €", fmtNumGR(customer.monthly_limit, 2)],
        ["Υπόλοιπο €", fmtNumGR(customer.remaining, 2)],
        [],
        ["ΣΥΝΟΛΙΚΕΣ ΚΙΝΗΣΕΙΣ"],
        ["Ημερομηνία", "Είδη", "Καθαρή €", "ΦΠΑ €", "Σύνολο €", "Χρεώθηκε €"],
        ...sales.map((s) => [
            fmtDate(s.created_at),
            s.line_items.length,
            fmtNumGR(s.subtotal_net, 2),
            fmtNumGR(s.total_vat, 2),
            fmtNumGR(s.total_gross, 2),
            fmtNumGR(s.amount_charged, 2),
        ]),
        // Grand total row
        [
            "ΣΥΝΟΛΑ",
            totalItems,
            fmtNumGR(totalNet, 2),
            fmtNumGR(totalVat, 2),
            fmtNumGR(totalGross, 2),
            fmtNumGR(totalCharged, 2),
        ],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(headerRows);
    ws1["!cols"] = [
        { wch: 22 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Κινήσεις");

    const detailRows = [
        ["Ημερομηνία", "Κωδικός", "Είδος", "Ποσότητα", "Τιμή €", "ΦΠΑ%", "Σύνολο €"],
    ];
    let detailQty = 0;
    let detailGross = 0;
    for (const s of sales) {
        for (const li of s.line_items) {
            const lineGross = Number(li.unit_price) * Number(li.quantity);
            detailQty += Number(li.quantity) || 0;
            detailGross += lineGross;
            detailRows.push([
                fmtDate(s.created_at),
                li.item_code,
                li.description,
                fmtNumGR(li.quantity, 0),
                fmtNumGR(li.unit_price, 2),
                li.vat_rate,
                fmtNumGR(lineGross, 2),
            ]);
        }
    }
    // Grand total row for the details sheet
    detailRows.push([
        "ΣΥΝΟΛΑ",
        "",
        "",
        fmtNumGR(detailQty, 0),
        "",
        "",
        fmtNumGR(detailGross, 2),
    ]);
    const ws2 = XLSX.utils.aoa_to_sheet(detailRows);
    ws2["!cols"] = [
        { wch: 18 },
        { wch: 10 },
        { wch: 32 },
        { wch: 10 },
        { wch: 12 },
        { wch: 8 },
        { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Αναλυτικά");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadFile(
        new Blob([buf], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `pelaths-${customer.code}${fileSuffix || ""}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
}
